import { createSlice } from "@reduxjs/toolkit";

/**
 * Filter Options Slice
 * 
 * Stores common filter dropdown data (departments, designations, etc.)
 * Fetched once and reused across all pages - no repeated API calls!
 */

const initialState = {
  // Department options
  departments: {
    data: [],
    loading: false,
    loaded: false, // Track if already fetched
    error: null,
  },
  
  // Designation options
  designations: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Designation options by department (for dependency filtering)
  designationsByDepartment: {
    data: {}, // { [departmentId]: [designations] }
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Role options
  roles: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Branch options
  branches: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Category options
  categories: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Vendor options
  vendors: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Staff options
  staff: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Customer options
  customers: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Lead Source options
  leadSources: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
  
  // Lead Status options
  leadStatuses: {
    data: [],
    loading: false,
    loaded: false,
    error: null,
  },
};

const filterOptionsSlice = createSlice({
  name: "filterOptions",
  initialState,
  reducers: {
    // Department actions
    setDepartmentsLoading: (state) => {
      state.departments.loading = true;
      state.departments.error = null;
    },
    setDepartmentsSuccess: (state, action) => {
      state.departments.data = action.payload;
      state.departments.loading = false;
      state.departments.loaded = true;
      state.departments.error = null;
    },
    setDepartmentsError: (state, action) => {
      state.departments.loading = false;
      state.departments.error = action.payload;
    },
    
    // Designation actions
    setDesignationsLoading: (state) => {
      state.designations.loading = true;
      state.designations.error = null;
    },
    setDesignationsSuccess: (state, action) => {
      state.designations.data = action.payload;
      state.designations.loading = false;
      state.designations.loaded = true;
      state.designations.error = null;
    },
    setDesignationsError: (state, action) => {
      state.designations.loading = false;
      state.designations.error = action.payload;
    },
    
    // Designations by department actions
    setDesignationsByDepartmentLoading: (state) => {
      state.designationsByDepartment.loading = true;
      state.designationsByDepartment.error = null;
    },
    setDesignationsByDepartmentSuccess: (state, action) => {
      const { departmentId, data } = action.payload;
      state.designationsByDepartment.data[departmentId] = data;
      state.designationsByDepartment.loading = false;
      state.designationsByDepartment.loaded = true;
      state.designationsByDepartment.error = null;
    },
    setDesignationsByDepartmentError: (state, action) => {
      state.designationsByDepartment.loading = false;
      state.designationsByDepartment.error = action.payload;
    },
    
    // Role actions
    setRolesLoading: (state) => {
      state.roles.loading = true;
      state.roles.error = null;
    },
    setRolesSuccess: (state, action) => {
      state.roles.data = action.payload;
      state.roles.loading = false;
      state.roles.loaded = true;
      state.roles.error = null;
    },
    setRolesError: (state, action) => {
      state.roles.loading = false;
      state.roles.error = action.payload;
    },
    
    // Branch actions
    setBranchesLoading: (state) => {
      state.branches.loading = true;
      state.branches.error = null;
    },
    setBranchesSuccess: (state, action) => {
      state.branches.data = action.payload;
      state.branches.loading = false;
      state.branches.loaded = true;
      state.branches.error = null;
    },
    setBranchesError: (state, action) => {
      state.branches.loading = false;
      state.branches.error = action.payload;
    },
    
    // Category actions
    setCategoriesLoading: (state) => {
      state.categories.loading = true;
      state.categories.error = null;
    },
    setCategoriesSuccess: (state, action) => {
      state.categories.data = action.payload;
      state.categories.loading = false;
      state.categories.loaded = true;
      state.categories.error = null;
    },
    setCategoriesError: (state, action) => {
      state.categories.loading = false;
      state.categories.error = action.payload;
    },
    
    // Vendor actions
    setVendorsLoading: (state) => {
      state.vendors.loading = true;
      state.vendors.error = null;
    },
    setVendorsSuccess: (state, action) => {
      state.vendors.data = action.payload;
      state.vendors.loading = false;
      state.vendors.loaded = true;
      state.vendors.error = null;
    },
    setVendorsError: (state, action) => {
      state.vendors.loading = false;
      state.vendors.error = action.payload;
    },
    
    // Staff actions
    setStaffLoading: (state) => {
      state.staff.loading = true;
      state.staff.error = null;
    },
    setStaffSuccess: (state, action) => {
      state.staff.data = action.payload;
      state.staff.loading = false;
      state.staff.loaded = true;
      state.staff.error = null;
    },
    setStaffError: (state, action) => {
      state.staff.loading = false;
      state.staff.error = action.payload;
    },
    
    // Customer actions
    setCustomersLoading: (state) => {
      state.customers.loading = true;
      state.customers.error = null;
    },
    setCustomersSuccess: (state, action) => {
      state.customers.data = action.payload;
      state.customers.loading = false;
      state.customers.loaded = true;
      state.customers.error = null;
    },
    setCustomersError: (state, action) => {
      state.customers.loading = false;
      state.customers.error = action.payload;
    },
    
    // Lead Source actions
    setLeadSourcesLoading: (state) => {
      state.leadSources.loading = true;
      state.leadSources.error = null;
    },
    setLeadSourcesSuccess: (state, action) => {
      state.leadSources.data = action.payload;
      state.leadSources.loading = false;
      state.leadSources.loaded = true;
      state.leadSources.error = null;
    },
    setLeadSourcesError: (state, action) => {
      state.leadSources.loading = false;
      state.leadSources.error = action.payload;
    },
    
    // Lead Status actions
    setLeadStatusesLoading: (state) => {
      state.leadStatuses.loading = true;
      state.leadStatuses.error = null;
    },
    setLeadStatusesSuccess: (state, action) => {
      state.leadStatuses.data = action.payload;
      state.leadStatuses.loading = false;
      state.leadStatuses.loaded = true;
      state.leadStatuses.error = null;
    },
    setLeadStatusesError: (state, action) => {
      state.leadStatuses.loading = false;
      state.leadStatuses.error = action.payload;
    },
    
    // Clear all filter options
    clearAllFilterOptions: (state) => {
      return initialState;
    },
    
    // Refresh specific option (force reload)
    refreshOption: (state, action) => {
      const optionType = action.payload; // e.g., 'departments'
      if (state[optionType]) {
        state[optionType].loaded = false;
        if (optionType === 'designationsByDepartment') {
          state[optionType].data = {};
        } else {
          state[optionType].data = [];
        }
      }
    },
  },
});

export const {
  setDepartmentsLoading,
  setDepartmentsSuccess,
  setDepartmentsError,
  setDesignationsLoading,
  setDesignationsSuccess,
  setDesignationsError,
  setDesignationsByDepartmentLoading,
  setDesignationsByDepartmentSuccess,
  setDesignationsByDepartmentError,
  setRolesLoading,
  setRolesSuccess,
  setRolesError,
  setBranchesLoading,
  setBranchesSuccess,
  setBranchesError,
  setCategoriesLoading,
  setCategoriesSuccess,
  setCategoriesError,
  setVendorsLoading,
  setVendorsSuccess,
  setVendorsError,
  setStaffLoading,
  setStaffSuccess,
  setStaffError,
  setCustomersLoading,
  setCustomersSuccess,
  setCustomersError,
  setLeadSourcesLoading,
  setLeadSourcesSuccess,
  setLeadSourcesError,
  setLeadStatusesLoading,
  setLeadStatusesSuccess,
  setLeadStatusesError,
  clearAllFilterOptions,
  refreshOption,
} = filterOptionsSlice.actions;

// Selectors with safety checks
export const selectDepartments = (state) => state?.filterOptions?.departments || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectDesignations = (state) => state?.filterOptions?.designations || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectDesignationsByDepartment = (state) => state?.filterOptions?.designationsByDepartment || {
  data: {},
  loading: false,
  loaded: false,
  error: null,
};
export const selectRoles = (state) => state?.filterOptions?.roles || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectBranches = (state) => state?.filterOptions?.branches || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectCategories = (state) => state?.filterOptions?.categories || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectVendors = (state) => state?.filterOptions?.vendors || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectStaff = (state) => state?.filterOptions?.staff || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectCustomers = (state) => state?.filterOptions?.customers || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectLeadSources = (state) => state?.filterOptions?.leadSources || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};
export const selectLeadStatuses = (state) => state?.filterOptions?.leadStatuses || {
  data: [],
  loading: false,
  loaded: false,
  error: null,
};

export default filterOptionsSlice.reducer;

